<template>
  <div class="care-page">
    <section class="care-hero reveal">
      <div class="care-hero__media ratio-9x3">
        <img
          class="care-hero__image"
          :src="page.hero.image"
          alt=""
          loading="eager"
        >
      </div>
      <div class="care-hero__overlay"></div>
      <div class="care-hero__content container">
        <div class="care-hero__title-wrap">
          <p class="care-hero__pretitle">{{ page.hero.pretitle }}</p>
          <h1 class="care-hero__title">{{ page.hero.title }}</h1>
        </div>
      </div>
    </section>

    <page-trail
      :items="[
        { label: $t('common.home'), to: '/' },
        { label: $t('nav.knowledge'), to: '/knowledge' },
        { label: $t('common.care'), to: '/care' }
      ]"
      :back-label="$t('common.backToKnowledge')"
      back-to="/knowledge"
    />

    <section class="care-guide reveal">
      <div class="container container-narrow">
        <header class="care-title-block">
          <p class="care-title-block__pretitle">{{ page.guide.pretitle }}</p>
          <h2 class="care-title-block__title">{{ page.guide.title }}</h2>
        </header>
        <div class="care-decor-border">
          <div class="care-wysiwyg">
            <p>{{ page.guide.intro }}</p>
            <ol>
              <li v-for="(step, index) in page.guide.steps" :key="'step-' + index">
                {{ step }}
              </li>
            </ol>
            <p>
              <strong>{{ page.guide.frequencyTitle }}</strong>
              {{ ' ' + page.guide.frequencyBody }}
            </p>
          </div>
        </div>
      </div>
    </section>

    <section v-if="page.video.src" class="care-video reveal" id="Cleaning">
      <div class="container">
        <header class="care-title-block">
          <p class="care-title-block__pretitle">{{ page.video.pretitle }}</p>
          <h2 class="care-title-block__title">{{ page.video.title }}</h2>
        </header>
        <div class="care-video__player">
          <div class="ratio-16x9 care-video__ratio">
            <video
              ref="careVideo"
              class="care-video__element"
              :src="page.video.src"
              :poster="page.video.poster"
              controls
              muted
              playsinline
              @play="videoPlaying = true"
            ></video>
            <div
              v-show="!videoPlaying"
              class="care-video__thumbnail"
              role="button"
              tabindex="0"
              :aria-label="$t('common.playVideo')"
              @click="playVideo"
              @keydown.enter.prevent="playVideo"
              @keydown.space.prevent="playVideo"
            >
              <img :src="page.video.poster" alt="">
              <button type="button" class="care-video__play" :aria-label="$t('common.playVideo')" @click.stop="playVideo">
                <svg width="35" height="35" viewBox="0 0 35 35" aria-hidden="true">
                  <path fill="currentColor" d="M6,0 L31,17.5 L6,35 L6,0 Z M9,5.761 L9,29.238 L25.768,17.5 L9,5.761 Z" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </section>

    <aside class="care-contact reveal">
      <div class="care-contact__row">
        <div class="care-contact__image">
          <img :src="page.contact.image" alt="" loading="lazy">
        </div>
        <div class="care-contact__panel">
          <h2 class="care-contact__title">{{ page.contact.title }}</h2>
          <form class="care-form" @submit.prevent="submitForm">
            <div class="care-form__field care-form__field--full">
              <label for="care-email">{{ $t('common.email') }} <span aria-hidden="true">*</span></label>
              <input id="care-email" v-model="form.email" type="email" required>
            </div>
            <div class="care-form__row care-form__row--zip">
              <div class="care-form__field care-form__field--zip">
                <label for="care-zip">{{ $t('common.zipCode') }} <span aria-hidden="true">*</span></label>
                <input id="care-zip" v-model="form.zip" type="text" required>
              </div>
              <div class="care-form__field care-form__field--country">
                <label for="care-country">{{ $t('common.country') }} <span aria-hidden="true">*</span></label>
                <select id="care-country" v-model="form.country" required>
                  <option disabled value="">{{ $t('common.pleaseSelect') }}</option>
                  <option v-for="country in page.contact.countries" :key="country" :value="country">
                    {{ country }}
                  </option>
                </select>
              </div>
            </div>
            <div class="care-form__field">
              <label for="care-phone">{{ $t('common.phone') }} <span aria-hidden="true">*</span></label>
              <input id="care-phone" v-model="form.phone" type="text" required>
            </div>
            <div class="care-form__field">
              <label for="care-name">{{ $t('common.name') }} <span aria-hidden="true">*</span></label>
              <input id="care-name" v-model="form.name" type="text" required>
            </div>
            <div class="care-form__field care-form__field--full">
              <label for="care-message">{{ $t('common.messageToUs') }}</label>
              <textarea id="care-message" v-model="form.message" rows="3"></textarea>
            </div>
            <button type="submit" class="care-form__submit" :disabled="submitting">
              <svg width="35" height="35" viewBox="0 0 35 35" aria-hidden="true">
                <path fill="currentColor" d="M34.064,0.007 C34.369,-0.033 34.651,0.102 34.824,0.325 L34.754,0.246 C34.788,0.28 34.819,0.316 34.846,0.354 C34.863,0.377 34.878,0.402 34.892,0.427 C34.895,0.432 34.897,0.436 34.9,0.441 C34.986,0.598 35.022,0.784 34.987,0.977 C34.985,0.984 34.984,0.99 34.983,0.997 C34.975,1.034 34.965,1.072 34.952,1.11 L23.285,34.442 C23.036,35.154 22.044,35.195 21.738,34.505 L15.201,19.797 L0.495,13.262 C-0.146,12.978 -0.156,12.102 0.416,11.779 L0.558,11.715 L33.89,0.048 C33.898,0.045 33.907,0.042 33.916,0.04 C33.935,0.034 33.955,0.028 33.975,0.024 C33.997,0.018 34.019,0.014 34.041,0.01 C34.047,0.01 34.053,0.009 34.059,0.008 C34.065,0.008 34.07,0.008 34.064,0.007 Z M32.17,4.008 L16.826,19.351 L22.406,31.908 L32.17,4.008 Z M30.995,2.825 L3.09,12.593 L15.648,18.173 L30.995,2.825 Z" />
              </svg>
              {{ submitting ? $t('common.submitting') : $t('common.submit') }}
            </button>
            <p v-if="formFeedback" class="care-form__feedback">{{ formFeedback }}</p>
          </form>
        </div>
      </div>
    </aside>
  </div>
</template>

<script>
import site from '../mock/strongova'
import PageTrail from '../components/PageTrail.vue'
import { fetchPublicPageSections, resolveAssetUrl, submitInquiry } from '../api/public'

export default {
  name: 'CarePage',
  components: {
    PageTrail
  },
  data () {
    return {
      page: { ...site.carePage },
      videoPlaying: false,
      submitting: false,
      formFeedback: '',
      form: {
        email: '',
        zip: '',
        country: '',
        phone: '',
        name: '',
        message: ''
      }
    }
  },
  created () {
    fetchPublicPageSections({ pageKey: 'care' })
      .then(sections => {
        if (Array.isArray(sections) && sections.length) {
          this.applyPageSections(sections)
        }
      })
      .catch(() => {
        this.page = { ...site.carePage }
      })
  },
  methods: {
    applyPageSections (sections) {
      const page = {
        ...site.carePage,
        hero: { ...site.carePage.hero },
        guide: {
          ...site.carePage.guide,
          steps: [...site.carePage.guide.steps]
        },
        video: { ...site.carePage.video },
        contact: { ...site.carePage.contact }
      }
      const hero = sections.find(item => item.sectionKey === 'hero')
      const guide = sections.find(item => item.sectionKey === 'intro')
      const video = sections.find(item => item.sectionKey === 'video')
      const contact = sections.find(item => item.sectionKey === 'contact')

      if (hero) {
        page.hero.title = hero.title || page.hero.title
        page.hero.pretitle = hero.description || page.hero.pretitle
        page.hero.image = resolveAssetUrl(hero.image, page.hero.image)
      }

      if (guide) {
        const lines = String(guide.description || '')
          .split('\n')
          .map(line => line.trim())
          .filter(Boolean)
        page.guide.title = guide.title || page.guide.title
        if (lines.length) page.guide.intro = lines[0]
        if (lines.length > 1) page.guide.steps = lines.slice(1)
      }

      if (video) {
        page.video.title = video.title || page.video.title
        page.video.pretitle = video.description && !video.description.startsWith('/') ? video.description : page.video.pretitle
        page.video.poster = resolveAssetUrl(video.image, page.video.poster)
        if (video.description && video.description.startsWith('/')) page.video.src = video.description
      }

      if (contact) {
        page.contact.title = contact.title || page.contact.title
        page.contact.image = resolveAssetUrl(contact.image, page.contact.image)
      }

      this.page = page
    },
    playVideo () {
      const video = this.$refs.careVideo
      if (!video) return
      this.videoPlaying = true
      video.play()
    },
    submitForm () {
      if (this.submitting) return
      this.submitting = true
      this.formFeedback = ''

      submitInquiry({
        source: 'care',
        firstName: this.form.name,
        email: this.form.email,
        zip: this.form.zip,
        country: this.form.country,
        phone: this.form.phone,
        message: this.form.message,
        requestType: 'care_contact'
      })
        .then(() => {
          this.formFeedback = 'Thank you. Your message has been submitted.'
          this.form = {
            email: '',
            zip: '',
            country: '',
            phone: '',
            name: '',
            message: ''
          }
        })
        .catch(() => {
          this.formFeedback = 'Sorry, the message could not be submitted. Please try again later.'
        })
        .then(() => {
          this.submitting = false
        })
    }
  }
}
</script>

<style>
.care-page {
  background: #f6f1e9;
  color: #0f0f0f;
}

.ratio-9x3 {
  aspect-ratio: 9 / 3;
}

.ratio-16x9 {
  aspect-ratio: 16 / 9;
}

.container-narrow {
  width: min(69.1875rem, calc(100vw - 2rem));
  margin: 0 auto;
}

@media screen and (min-width: 768px) {
  .container-narrow {
    max-width: 70.4375rem;
  }
}

/* —— Hero —— */
.care-hero {
  position: relative;
  overflow: hidden;
  background: #0f0f0f;
}

.care-hero__media {
  position: relative;
  width: 100%;
  min-height: clamp(15.625rem, 32vw, 28rem);
  background: #111;
}

.care-hero__image {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.care-hero__overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  pointer-events: none;
  background: linear-gradient(90deg, rgba(15, 15, 15, 0), rgba(15, 15, 15, 0.9));
}

.care-hero__content {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  padding: 2.5rem 0;
}

@media screen and (min-width: 768px) {
  .care-hero__content {
    align-items: center;
    justify-content: flex-end;
  }
}

.care-hero__title-wrap {
  width: 100%;
  max-width: 22rem;
  margin: 0 auto;
  text-align: center;
  color: #f5f3ef;
}

@media screen and (min-width: 768px) {
  .care-hero__title-wrap {
    margin: 0;
    margin-left: auto;
    text-align: left;
  }
}

.care-hero__pretitle {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  letter-spacing: 0.08em;
  line-height: 1.55;
  text-transform: uppercase;
  color: rgba(245, 243, 239, 0.82);
}

.care-hero__title {
  margin: 0;
  font-size: clamp(1.875rem, 2.6vw, 2.5rem);
  font-weight: 400;
  line-height: 1.08;
  letter-spacing: 0.02em;
}

.care-page .page-trail {
  background: #f6f1e9;
  border-bottom-color: rgba(15, 15, 15, 0.1);
}

/* —— Guide —— */
.care-guide {
  padding: clamp(2.5rem, 5vw, 4rem) 0;
}

.care-title-block {
  margin-bottom: clamp(1.5rem, 3vw, 2rem);
}

.care-title-block__pretitle {
  margin: 0 0 0.5rem;
  font-size: 1rem;
  line-height: 1.55;
  font-weight: 400;
  letter-spacing: 0.04em;
}

.care-title-block__title {
  margin: 0;
  font-size: clamp(1.75rem, 2.4vw, 2.25rem);
  font-weight: 400;
  line-height: 1.15;
}

.care-decor-border {
  position: relative;
}

@media screen and (min-width: 768px) {
  .care-decor-border {
    margin-left: calc(5rem + 50px);
  }

  .care-decor-border::before {
    content: "";
    position: absolute;
    top: 0.625rem;
    left: calc(-5rem - 50px);
    width: 5rem;
    height: 1px;
    background: #797979;
  }
}

.care-wysiwyg {
  font-size: 1.0625rem;
  line-height: 1.65;
  color: rgba(15, 15, 15, 0.88);
}

.care-wysiwyg p {
  margin: 0 0 1.25rem;
}

.care-wysiwyg ol {
  margin: 0 0 1.25rem;
  padding-left: 1.35rem;
}

.care-wysiwyg li + li {
  margin-top: 0.35rem;
}

.care-wysiwyg strong {
  font-weight: 600;
}

/* —— Video —— */
.care-video {
  padding: 0 0 clamp(2.5rem, 5vw, 4rem);
}

.care-video .container {
  width: min(1360px, calc(100vw - 64px));
}

.care-video__player {
  margin-top: 0.5rem;
}

.care-video__ratio {
  position: relative;
  overflow: hidden;
  background: #111;
}

.care-video__element {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.care-video__thumbnail {
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: pointer;
}

.care-video__thumbnail img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  filter: brightness(50%);
}

.care-video__play {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  padding: 0.875rem 1.25rem;
  border: 0;
  background: none;
  color: #fff;
  cursor: pointer;
}

.care-video__play::before {
  content: "";
  position: absolute;
  inset: 0;
  border-top: 1px solid currentColor;
  border-bottom: 1px solid currentColor;
  opacity: 0.4;
  pointer-events: none;
  transition: transform 0.2s ease;
}

.care-video__play:hover::before {
  transform: scaleX(0.7);
}

.care-video__play svg {
  position: relative;
  width: 35px;
  height: 35px;
}

/* —— Contact —— */
.care-contact {
  margin-top: 0;
}

.care-contact__row {
  display: grid;
  grid-template-columns: 1fr;
}

@media screen and (min-width: 768px) {
  .care-contact__row {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}

.care-contact__image {
  position: relative;
  min-height: 12.5rem;
  overflow: hidden;
}

.care-contact__image img {
  width: 100%;
  height: 100%;
  min-height: 12.5rem;
  object-fit: cover;
}

@media screen and (min-width: 768px) {
  .care-contact__image,
  .care-contact__image img {
    min-height: 100%;
    height: 100%;
  }
}

.care-contact__panel {
  padding: 1.875rem 1.5625rem;
  background: #e2e2e2;
  color: #0f0f0f;
}

@media screen and (min-width: 768px) {
  .care-contact__panel {
    padding: 4.6875rem;
  }
}

.care-contact__title {
  margin: 0 0 1.75rem;
  font-size: clamp(1.75rem, 2.4vw, 2.25rem);
  font-weight: 400;
  line-height: 1.15;
}

.care-form {
  display: grid;
  gap: 0;
}

.care-form__row--zip {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 3fr);
  gap: 1rem;
}

.care-form__field {
  margin-bottom: 1rem;
}

.care-form label {
  display: block;
  margin-bottom: 0.35rem;
  font-size: 0.9375rem;
}

.care-form label span {
  color: inherit;
}

.care-form input,
.care-form select,
.care-form textarea {
  width: 100%;
  min-height: 3.75rem;
  padding: 0.75rem 0.625rem;
  border: 1px solid rgba(15, 15, 15, 0.4);
  border-radius: 0;
  background: transparent;
  color: #0f0f0f;
  font: inherit;
  font-size: 1.0625rem;
  line-height: 1.53;
}

.care-form textarea {
  min-height: auto;
  resize: vertical;
}

.care-form input:focus,
.care-form select:focus,
.care-form textarea:focus {
  outline: none;
  box-shadow: none;
  border-color: #0f0f0f;
}

.care-form__submit {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  min-height: 3.25rem;
  margin-top: 0.25rem;
  padding: 0 1.5rem;
  border: 0;
  background: #0f0f0f;
  color: #fff;
  font-size: 1.0625rem;
  cursor: pointer;
  transition: background 0.2s ease;
}

.care-form__submit:hover {
  background: #2a2622;
}

.care-form__submit svg {
  width: 1.35rem;
  height: 1.35rem;
  flex-shrink: 0;
}

.care-form__feedback {
  margin: 1rem 0 0;
  font-size: 0.9375rem;
  color: rgba(15, 15, 15, 0.72);
}

@media (max-width: 767px) {
  .care-hero__content .container {
    width: min(100%, calc(100vw - 2rem));
  }

  .care-video .container {
    width: min(100%, calc(100vw - 2rem));
  }
}
</style>
